import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { getOrder } from "../lib/dataStore";
import { orderSubtotal, orderDiscount, orderShipping, orderGrandTotal, getCarrierType, getCarrierTrackingUrl } from "../lib/orderHelpers";
import {
  Package, Search, Truck, CheckCircle2, Clock, MapPin, ExternalLink,
  Copy, Share2, ArrowLeft, ShieldCheck, AlertCircle, Building2
} from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "../lib/company";

export default function TrackOrder() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  const [query, setQuery] = useState(orderId || searchParams.get("id") || searchParams.get("consignment") || "");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(query);

  const [directOrder, setDirectOrder] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const targetId = orderId || searchParams.get("id") || searchParams.get("consignment");
      if (targetId) {
        const single = await getOrder(targetId.trim());
        if (active) {
          setDirectOrder(single || null);
          setLoading(false);
        }
        return;
      }
      if (active) {
        setDirectOrder(null);
        setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [orderId, searchParams]);

  useEffect(() => {
    if (orderId) {
      setSearchQuery(orderId);
      setQuery(orderId);
    }
  }, [orderId]);

  const foundOrder = useMemo(() => {
    return directOrder || null;
  }, [directOrder]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearchQuery(query.trim());
    nav(`/track/${query.trim()}`);
  };

  // Build or compute milestones based on order state
  const milestones = useMemo(() => {
    if (!foundOrder) return [];
    const status = foundOrder.status || "New";
    const cons = foundOrder.consignmentNumber || foundOrder.trackingNumber || "";
    const createdDate = new Date(foundOrder.createdAt?.seconds ? foundOrder.createdAt.seconds * 1000 : foundOrder.createdAt);
    const dateStr = isNaN(createdDate) ? "Recently" : createdDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

    // Step status helper: 'completed', 'current', or 'pending'
    const getStepState = (stepIdx) => {
      if (status === "Cancelled") return stepIdx === 0 ? "completed" : "pending";
      if (status === "Delivered") return "completed";
      if (status === "Shipped") return stepIdx <= 3 ? (stepIdx === 3 ? "current" : "completed") : "pending";
      if (status === "Confirmed") return stepIdx <= 1 ? (stepIdx === 1 ? "current" : "completed") : "pending";
      // "New"
      return stepIdx === 0 ? "current" : "pending";
    };

    const courier = foundOrder.courierName || "Others";
    const isIndiaPost = courier.toLowerCase().includes("india post") || courier.toLowerCase().includes("post");
    const city = foundOrder.city || "Destination";
    const pincode = foundOrder.pincode ? `(${foundOrder.pincode})` : "";

    return [
      {
        title: "Order Placed",
        location: `${COMPANY.name} Online Store`,
        time: dateStr,
        state: getStepState(0),
        description: "Your order has been recorded and sent to our artisan studio for crafting and verification.",
        icon: Package
      },
      {
        title: "Order Confirmed & Quality Checked",
        location: `${COMPANY.city} Studio`,
        time: status !== "New" ? "Completed" : "In Progress",
        state: getStepState(1),
        description: `Items inspected, hand-packaged, and queued for ${courier} consignment dispatch.`,
        icon: ShieldCheck
      },
      {
        title: "Consignment Dispatched",
        location: foundOrder.dispatchPostOffice || `${COMPANY.city} Dispatch Facility`,
        time: cons ? (foundOrder.dispatchDate || "Dispatched") : "Pending dispatch",
        state: getStepState(2),
        description: cons ? `Booked via ${courier} with Consignment ID: ${cons}` : "Awaiting consignment ID registration with courier partner.",
        icon: Building2
      },
      {
        title: isIndiaPost ? "In Transit via India Post Network" : `In Transit via ${courier} Network`,
        location: isIndiaPost ? `National Sorting Hub → ${city} Sorting Hub` : `Hub Facility → ${city} Sorting Facility`,
        time: status === "Shipped" || status === "Delivered" ? "En route to local delivery branch" : "Pending",
        state: getStepState(3),
        description: isIndiaPost
          ? "Package is moving through India Post mail bags and regional sorting hubs."
          : `Package is moving through ${courier} logistics network to destination sorting hub.`,
        icon: Truck
      },
      {
        title: "Out for Delivery & Delivered",
        location: `${city} ${pincode}`,
        time: status === "Delivered" ? "Delivered" : "Expected in 2–4 days",
        state: getStepState(4),
        description: status === "Delivered" ? "Package successfully delivered to customer address." : "Assigned to local delivery agent for doorstep delivery.",
        icon: CheckCircle2
      }
    ];
  }, [foundOrder]);

  const currentStepIndex = useMemo(() => {
    if (!foundOrder) return 0;
    const status = foundOrder.status;
    if (status === "Delivered") return 4;
    if (status === "Shipped") return 3;
    if (status === "Confirmed") return 1;
    return 0;
  }, [foundOrder]);

  const progressPercent = Math.min(100, Math.max(10, (currentStepIndex / 4) * 100));

  const copyTrackingLink = () => {
    const link = `${window.location.origin}/track/${foundOrder.id}`;
    navigator.clipboard.writeText(link);
    toast.success("Tracking link copied to clipboard!");
  };

  const copyConsignment = () => {
    const cons = foundOrder.consignmentNumber || foundOrder.trackingNumber || "";
    if (cons) {
      navigator.clipboard.writeText(cons);
      toast.success(`Consignment ID '${cons}' copied!`);
    }
  };

  const openCarrierPortal = () => {
    const cons = foundOrder?.consignmentNumber || foundOrder?.trackingNumber || "";
    const carrier = getCarrierType(foundOrder?.courierName);
    const url = getCarrierTrackingUrl(foundOrder?.courierName, cons);
    if (cons) {
      navigator.clipboard.writeText(cons);
      toast.success(`${carrier} Tracking ID '${cons}' copied!`);
    }
    if (url) {
      window.open(url, "_blank");
    } else {
      toast.info(`Carrier: ${carrier}`);
    }
  };

  const openSpeedPost = () => {
    const cons = foundOrder?.consignmentNumber || foundOrder?.trackingNumber || "";
    if (cons) {
      navigator.clipboard.writeText(cons);
      toast.success(`Consignment ID '${cons}' copied! Opening MySpeedPost.com...`);
    } else {
      toast.info("Opening MySpeedPost.com...");
    }
    window.open("https://myspeedpost.com/", "_blank");
  };

  const openIndiaPostPortal = () => {
    const cons = foundOrder?.consignmentNumber || foundOrder?.trackingNumber || "";
    if (cons) {
      navigator.clipboard.writeText(cons);
      toast.success("Consignment ID copied! Opening official India Post portal...");
    }
    window.open("https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx", "_blank");
  };

  const shareWhatsApp = () => {
    const cons = foundOrder.consignmentNumber || foundOrder.trackingNumber || "N/A";
    const link = `${window.location.origin}/track/${foundOrder.id}`;
    const text = encodeURIComponent(`📦 Track my Lineacraft order #${String(foundOrder.id || "").toUpperCase()}:\nConsignment No: ${cons}\nStatus: ${foundOrder.status}\nLink: ${link}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="mb-8 flex items-center justify-between">
        <Link to="/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="w-4 h-4" /> Back to My Orders
        </Link>
        <span className="overline text-terracotta font-medium">Order Shipment Tracker</span>
      </div>

      <div className="text-center mb-10">
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">Track Your Package</h1>
        <p className="text-muted-foreground max-w-xl mx-auto text-sm">
          Enter your Order ID or Post Office Consignment ID to see the full completed shipment path and live delivery status.
        </p>

        {/* Search Input Bar */}
        <form onSubmit={handleSearchSubmit} className="mt-6 max-w-lg mx-auto flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Order ID or Consignment ID (e.g. SP847291038IN)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-full text-sm outline-none focus:border-terracotta transition shadow-xs"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 bg-terracotta text-primary-foreground font-medium rounded-full text-sm hover:opacity-90 transition shrink-0"
          >
            Track
          </button>
        </form>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground">Loading tracking records…</div>
      ) : searchQuery && !foundOrder ? (
        <div className="border border-dashed border-border p-10 text-center rounded-2xl bg-card space-y-3">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-600 opacity-80" />
          <h2 className="font-serif text-xl">No shipment found</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            We couldn't find an order or consignment matching "<strong>{searchQuery}</strong>". Please double check your Order ID from your confirmation email or invoice.
          </p>
          <div className="pt-2">
            <Link to="/orders" className="text-sm text-terracotta underline underline-offset-4 font-medium">
              View all my orders →
            </Link>
          </div>
        </div>
      ) : foundOrder ? (
        <div className="space-y-8 animate-fade-up">
          {/* Main Shipment Card */}
          <div className="border border-border bg-card rounded-2xl p-6 md:p-8 shadow-xs">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-6">
              <div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">
                  Order #{String(foundOrder.id || "").toUpperCase()}
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold flex items-center gap-3">
                  Status: <span className="text-terracotta">{foundOrder.status}</span>
                </h2>
                <div className="text-xs text-muted-foreground mt-1">
                  Placed on {new Date(foundOrder.createdAt?.seconds ? foundOrder.createdAt.seconds * 1000 : foundOrder.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>

              {/* Consignment ID Box */}
              <div className="bg-muted/60 border border-border rounded-xl p-4 min-w-[240px]">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                  Post Office Consignment ID
                </div>
                {foundOrder.consignmentNumber || foundOrder.trackingNumber ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-base font-bold text-foreground">
                      {foundOrder.consignmentNumber || foundOrder.trackingNumber}
                    </span>
                    <button
                      onClick={copyConsignment}
                      title="Copy Consignment ID"
                      className="p-1.5 hover:bg-background rounded text-muted-foreground hover:text-foreground transition"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-amber-800 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 px-2.5 py-1 rounded font-medium inline-block">
                    Awaiting Post Office Registration
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-1">
                  Carrier: <strong>{foundOrder.courierName || "Others"}</strong>
                </div>
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="my-8 px-2">
              <div className="flex justify-between text-xs font-semibold text-muted-foreground mb-2">
                <span>Placed</span>
                <span>Confirmed</span>
                <span>Dispatched</span>
                <span>In Transit</span>
                <span>Delivered</span>
              </div>
              <div className="w-full bg-muted h-3 rounded-full overflow-hidden relative border border-border/50">
                <div
                  className="bg-terracotta h-full transition-all duration-700 ease-out rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Quick Actions Row */}
            <div className="flex flex-wrap gap-3 pt-2">
              {(() => {
                const carrier = getCarrierType(foundOrder.courierName);
                const isIndiaPost = carrier === "India Post";
                const isShiprocket = carrier === "Shiprocket";
                return (
                  <>
                    <button
                      onClick={openCarrierPortal}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-primary-foreground text-xs font-medium hover:opacity-90 transition shadow-xs"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {isShiprocket
                        ? "Track on Shiprocket Portal"
                        : isIndiaPost
                        ? "Verify on Official India Post Portal"
                        : (carrier === "Others" || carrier === "Other")
                        ? (foundOrder.courierCustomName ? `Track on ${foundOrder.courierCustomName}` : "Track with Carrier")
                        : `Track on ${carrier} Portal`}
                    </button>
                    {isIndiaPost && (
                      <button
                        onClick={openSpeedPost}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground text-background text-xs font-medium hover:bg-terracotta transition"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Check Status on MySpeedPost.com
                      </button>
                    )}
                  </>
                );
              })()}
              <button
                onClick={copyTrackingLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-border text-xs font-medium hover:bg-muted transition"
              >
                <Copy className="w-3.5 h-3.5 text-terracotta" /> Copy Tracking Link
              </button>
              <button
                onClick={shareWhatsApp}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 text-xs font-medium hover:bg-emerald-100 transition"
              >
                <Share2 className="w-3.5 h-3.5 text-emerald-600" /> Share via WhatsApp
              </button>
            </div>
          </div>

          {/* Completed Path / Milestone Journey Timeline */}
          <div className="border border-border bg-card rounded-2xl p-6 md:p-8 shadow-xs">
            <h3 className="font-serif text-2xl mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-terracotta" /> Completed Path &amp; Tracking History
            </h3>

            <div className="relative pl-6 md:pl-8 space-y-8 before:absolute before:left-2.5 md:before:left-3.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-border">
              {milestones.map((m, idx) => {
                const Icon = m.icon;
                const isDone = m.state === "completed";
                const isCurr = m.state === "current";

                return (
                  <div key={idx} className="relative flex items-start gap-4">
                    {/* Icon Bullet */}
                    <div
                      className={`absolute -left-6 md:-left-8 top-0 w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-xs border transition-all ${
                        isDone
                          ? "bg-terracotta text-primary-foreground border-terracotta shadow-xs"
                          : isCurr
                          ? "bg-background border-terracotta text-terracotta ring-4 ring-terracotta/20 animate-pulse"
                          : "bg-muted text-muted-foreground border-border"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>

                    <div className="flex-1 bg-muted/30 border border-border/80 rounded-xl p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <span className={`font-serif text-lg font-medium ${isDone || isCurr ? "text-foreground" : "text-muted-foreground"}`}>
                          {m.title}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono bg-background px-2.5 py-1 rounded border border-border">
                          {m.time}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-terracotta flex items-center gap-1 mb-1.5">
                        <MapPin className="w-3 h-3" /> {m.location}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {m.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Custom Admin Logs / Checkpoints if available */}
            {foundOrder.trackingLogs && foundOrder.trackingLogs.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border">
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                  Additional Post Office Sorting Updates
                </h4>
                <div className="space-y-2">
                  {foundOrder.trackingLogs.map((log, lIdx) => (
                    <div key={lIdx} className="text-xs bg-muted/50 p-3 rounded-lg border border-border/60 flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold text-foreground">{log.location || "Post Office Facility"}</span>
                        <p className="text-muted-foreground mt-0.5">{log.message}</p>
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">{log.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Package Details Section */}
          <div className="border border-border bg-card rounded-2xl p-6 md:p-8 shadow-xs">
            <h3 className="font-serif text-xl mb-4">Package Contents &amp; Destination</h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="overline text-muted-foreground mb-2">Delivery Address</div>
                <div className="font-medium">{foundOrder.customerName}</div>
                <div className="text-muted-foreground leading-relaxed mt-1">
                  {foundOrder.address}<br />
                  {foundOrder.city}, {foundOrder.state || "India"} — {foundOrder.pincode}<br />
                  Phone: {foundOrder.phone}
                </div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Items ({foundOrder.items?.length || 0})</div>
                <ul className="space-y-2 mb-4">
                  {foundOrder.items?.map((it, idx) => {
                    const itemPrice = Number(it.price || 0);
                    const itemMrp = Number(it.mrp || itemPrice);
                    const hasItemDiscount = itemMrp > itemPrice;
                    return (
                      <li key={idx} className="flex justify-between items-start text-xs border-b border-border/60 pb-2">
                        <div>
                          <strong className="font-medium text-foreground block">{it.name}</strong>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                            {it.variantTag && it.variantTag !== "Default" && <span>Variant: {it.variantTag}</span>}
                            <span>Qty: {it.quantity}</span>
                            {hasItemDiscount && (
                              <span className="line-through text-muted-foreground/70">MRP ₹{itemMrp}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right font-mono">
                          <span className="font-semibold text-foreground">₹{itemPrice * it.quantity}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Pricing & Discount Breakdown */}
                {(() => {
                  const sub = orderSubtotal(foundOrder);
                  const disc = orderDiscount(foundOrder);
                  const shipping = orderShipping(foundOrder);
                  const finalTotal = orderGrandTotal(foundOrder);
                  const mrpSavings = (foundOrder.items || []).reduce((acc, it) => {
                    const price = Number(it.price || 0);
                    const mrp = Number(it.mrp || price);
                    return acc + (mrp > price ? (mrp - price) * Number(it.quantity || 1) : 0);
                  }, 0);
                  const totalSaved = disc + mrpSavings;

                  return (
                    <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 space-y-2 text-xs">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Items Subtotal</span>
                        <span className="font-mono font-medium text-foreground">₹{sub}</span>
                      </div>

                      {disc > 0 && (
                        <div className="flex justify-between text-emerald-700 dark:text-emerald-400 font-medium">
                          <span>Special Discount / Coupon</span>
                          <span className="font-price font-semibold">− ₹{disc}</span>
                        </div>
                      )}

                      {mrpSavings > 0 && (
                        <div className="flex justify-between text-emerald-700 dark:text-emerald-400 font-medium">
                          <span>MRP Catalog Savings</span>
                          <span className="font-price font-semibold">− ₹{mrpSavings}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-muted-foreground">
                        <span>Shipping Charge</span>
                        <span className="font-medium">
                          {shipping > 0 ? <span className="font-price font-semibold">₹{shipping}</span> : <span className="text-emerald-700 font-semibold">FREE</span>}
                        </span>
                      </div>

                      {totalSaved > 0 && (
                        <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 px-2.5 py-1 rounded text-[11px] font-semibold flex items-center justify-between">
                          <span>Total Savings on this order</span>
                          <span className="font-price font-bold">₹{totalSaved} OFF</span>
                        </div>
                      )}

                      <div className="pt-2 border-t border-border flex justify-between items-center text-sm font-semibold text-foreground">
                        <span className="text-base font-medium">Grand Total</span>
                        <span className="font-price text-2xl font-bold text-terracotta">₹{finalTotal}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
